import { memberScope } from '@/kinds/grant/schema'
import type { PersonResource } from '@/kinds/person/apply'
import { memberNameWith, personFor } from './people'
import type { GrantResource } from '@/kinds/grant/apply'

/**
 * "Keep Keira posted" (docs/sharing.md): a label `posted.<grant id>` = `1` on a
 * task or a project, written by the owner's devices and Prime, refused to a
 * member by the sync engine. A project's covers its tasks (the server does that
 * lookup). The label is never shown as a label.
 */
export const FOLLOW_PREFIX = 'posted.'

/**
 * "Nudge Alfie": a member writes a new value (the time) on a task's `nudge`
 * label; the server turns the change into the owner's push naming her grant,
 * at most once per task per ten minutes. The value is only a trigger.
 */
export const NUDGE_LABEL = 'nudge'

export const nudgePatch = (now: Date = new Date()): Record<string, string> => ({
  [NUDGE_LABEL]: now.toISOString(),
})

/**
 * "Nudge Keira": the owner's mirror. A new value (the time) on a task, project
 * or list's `nudge_members` label; the server turns a change from a device that
 * is not a member's into a push to the workspace's members, at most once per
 * item per ten minutes. The value is only a trigger. With `only` (a member's
 * grant id) it is "Share with…": the second word names the one member pushed,
 * limited per person.
 */
export const NUDGE_MEMBERS_LABEL = 'nudge_members'

export const nudgeMembersPatch = (now: Date = new Date(), only?: string): Record<string, string> => ({
  [NUDGE_MEMBERS_LABEL]: only ? `${now.toISOString()} ${only}` : now.toISOString(),
})

export const followKey = (grantId: string): string => FOLLOW_PREFIX + grantId

/**
 * The docs copies (internal/docsync): `doc` = `/` on the docs root page, the
 * file's path under docs/ on each copy. Written by the server at start.
 */
export const DOC_LABEL = 'doc'

/** A label that belongs to the system and is never shown or edited as one (`via` is a share link's id). */
export const systemLabel = (key: string): boolean =>
  key === 'via' ||
  key === NUDGE_LABEL ||
  key === NUDGE_MEMBERS_LABEL ||
  key === ADDED_BY ||
  key === DOC_LABEL ||
  key.startsWith(FOLLOW_PREFIX)

/**
 * A workspace member's device stamps the tasks she creates with her grant id, so
 * her Today can say what she added (the pulled resource does not carry its
 * creator). It is her own bookkeeping: never shown as a label.
 */
export const ADDED_BY = 'added_by'

/** Labels the app never lists as chips: the workspace, and the system's. */
export const hiddenLabel = (key: string): boolean => key === 'ws' || systemLabel(key)

export const isFollowing = (labels: Record<string, string>, grantId: string): boolean =>
  followKey(grantId) in labels

/** The label patch that switches it on or off. */
export const followPatch = (grantId: string, on: boolean): Record<string, string | null> => ({
  [followKey(grantId)]: on ? '1' : null,
})

export interface FollowMember {
  /** the first of the person's grants (a stable key for the row) */
  id: string
  /** every live grant of the person: the switch reads and writes all of them */
  ids: string[]
  name: string
}

/** The grant's login is live: not deleted, revoked or expired, and workspace scopes only. */
const liveMember = (g: GrantResource, now: Date): boolean => {
  if (g.deleted_at || g.status.revoked_at) return false
  if (g.spec.expires_at && new Date(g.spec.expires_at) <= now) return false
  return g.spec.scopes.length > 0 && g.spec.scopes.every((s) => memberScope(s) !== null)
}

/**
 * The workspace members who could be kept posted: live logins, one per person
 * (the person a grant links to, else its name; an unlinked grant with the same
 * name as a linked one joins it), by name.
 */
export const followMembers = (
  grants: GrantResource[],
  now: Date = new Date(),
  people: PersonResource[] = [],
): FollowMember[] => {
  const out: FollowMember[] = []
  const byKey = new Map<string, FollowMember>()
  const live = grants.filter((g) => liveMember(g, now))
  const keyed = live.map((g) => {
    const name = memberNameWith(g.spec, people)
    const person = g.spec.from ? personFor(people, g.spec.from) : undefined
    return { g, name, person: person && !person.spec.me ? `person:${person.id}` : '' }
  })
  // an unlinked grant joins the linked person of the same name
  const linkedByName = new Map<string, string>()
  for (const k of keyed) if (k.person) linkedByName.set(k.name.toLowerCase(), k.person)
  for (const k of keyed) {
    const key = k.person || linkedByName.get(k.name.toLowerCase()) || `name:${k.name.toLowerCase()}`
    const have = byKey.get(key)
    if (have) have.ids.push(k.g.id)
    else {
      const m = { id: k.g.id, ids: [k.g.id], name: k.name }
      byKey.set(key, m)
      out.push(m)
    }
  }
  return out
}
